import {
  Component,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
  OnChanges,
  Optional,
  Self,
  SimpleChanges,
} from '@angular/core';
import { NgControl, Validators } from '@angular/forms';
import { MatCheckboxChange } from '@angular/material/checkbox';
import {
  ControlValueAccessor,
  FormBuilder,
  FormGroup,
} from '@ngneat/reactive-forms';
import * as _ from 'lodash';

import { NestedFormComponent } from '@digital-platform/shared/ui';
import {
  DominantColorOptions,
  PhysicalComponentsSample,
  SpecialWasteProfile,
} from '@digital-platform/waste-profiles/domain';
import { Item } from '../waste-component-row/waste-component-row.component';

type PhysicalComponent = SpecialWasteProfile['physicalComponent'];
type PhysicalComponentForm = Omit<
  SpecialWasteProfile['physicalComponent'],
  'physicalComponents'
> & {
  physicalComponents: Item[];
};
@Component({
  selector: 'digital-platform-physical-characteristics',
  templateUrl: './physical-characteristics.component.html',
  styleUrls: ['./physical-characteristics.component.scss'],
})
export class PhysicalCharacteristicsComponent
  extends NestedFormComponent<PhysicalComponent, PhysicalComponentForm>
  implements
    OnInit,
    OnChanges,
    OnDestroy,
    ControlValueAccessor<PhysicalComponent>
{
  @HostBinding() class = 'digital-platform-physical-characteristics';
  @Input() profileStatus;
  @Input() isRequired = true;
  @Input() isLegacy = false;

  public form: FormGroup<any>;
  dominantOptions: DominantColorOptions = {
    dominantColor: [
      'Black',
      'Gray',
      'Brown',
      'White',
      'Green',
      'Red',
      'Yellow',
      'Blue',
      'Colorless',
    ],
  };
  @Input()
  public showOtherColor = false;
  public showOdorDescription = false;
  isToggleEnabled = false;
  sumOfTypical: number;
  isDominantColorSelected = false;

  labels = {
    name: 'Component Name',
    typicalOrActual: 'Typical / Actual %',
    min: 'Low %',
    max: 'High %',
  };

  constructor(
    @Optional() @Self() public controlDir: NgControl,
    private fb: FormBuilder
  ) {
    super(controlDir);
  }

  ngOnInit(): void {
    let physicalComponent: PhysicalComponentForm = {
      physicalComponents: null,
      dominantColor: null,
      otherColor: null,
      odorStrength: null,
      odorDescription: null,
    };

    if (this.value) {
      physicalComponent = this.mapInputValue(this.value);
      this.isDominantColorSelected = physicalComponent?.otherColor?.length > 0;
    }

    if (this.form) {
      this.form.patchValue(physicalComponent, {});
    } else {
      this.form = this.fb.group({
        physicalComponents: [
          physicalComponent.physicalComponents,
          () => {
            if (this.sumOfTypical > 100) {
              if (this.isLegacy === false) return { sumOver100: true };
              else return null;
            }

            return null;
          },
        ],
        dominantColor:
          this.isLegacy === false
            ? [physicalComponent.dominantColor, Validators.required]
            : [physicalComponent.dominantColor],
        otherColor: !this.isLegacy
          ? [
              physicalComponent?.otherColor,
              this.isDominantColorSelected ? Validators.required : [],
            ]
          : [physicalComponent?.otherColor],
        odorStrength:
          this.isLegacy === false
            ? [physicalComponent.odorStrength, Validators.required]
            : [physicalComponent.odorStrength],
        odorDescription: physicalComponent.odorDescription,
      });
    }
    this.form.get('physicalComponents').valueChanges.subscribe((value) => {
      this.validateTypicalOrActualPercent(value);
    });

    this.onOdorChanges();
    if (
      this.value?.physicalComponents.some(
        (x) =>
          (x.lowPercent !== undefined && x.lowPercent !== null) ||
          (x.highPercent !== undefined && x.highPercent !== null)
      )
    ) {
      this.isToggleEnabled = true;
    } else if (
      this.value?.physicalComponents.some(
        (x) =>
          x.typicalOrActualPercent !== undefined &&
          x.typicalOrActualPercent !== null
      )
    ) {
      this.isToggleEnabled = false;
    } else {
    }
    super.ngOnInit();
  }

  public mapInputValue(input: any): PhysicalComponentForm {
    return {
      ...input,
      physicalComponents: input.physicalComponents.map((component) => ({
        name: component?.name || '',
        typicalOrActual: component?.typicalOrActualPercent
          ? _.toString(component?.typicalOrActualPercent)
          : null,

        min:
          component?.lowPercent || component?.lowPercent === 0
            ? _.toString(component?.lowPercent)
            : null,

        max: component?.highPercent ? _.toString(component?.highPercent) : null,
        unitOfMeasure: 'percent',
      })),
    };
  }

  public mapOutputValue(value: PhysicalComponentForm): PhysicalComponent {
    const physicalComponents = (value.physicalComponents || []).map(
      (component) => ({
        name: component?.name || '',
        typicalOrActualPercent: component?.typicalOrActual
          ? _.toNumber(component?.typicalOrActual)
          : null,
        lowPercent: component?.min ? _.toNumber(component?.min) : null,
        highPercent: component?.max ? _.toNumber(component?.max) : null,
      })
    );

    const samples: PhysicalComponentsSample[] = [];
    for (const comp of physicalComponents) {
      if (comp.name) {
        samples.push(comp);
      }
    }
    return {
      ...value,
      ...{ physicalComponents: samples },
    };
  }

  errorHandling = (control: string, error: string) =>
    this.form.controls[control]?.touched &&
    this.form.controls[control].hasError(error);

  onOdorChanges = () => {
    const onOdorVal = this.form.get('odorStrength').value;
    if (
      onOdorVal === 'strong' ||
      onOdorVal === 'mild' ||
      onOdorVal === 'slight'
    ) {
      this.showOdorDescription = true;
      if (!this.isLegacy)
        this.form.get('odorDescription').setValidators([Validators.required]);
    } else {
      this.showOdorDescription = false;
    }
    this.form.get('odorStrength').valueChanges.subscribe((val) => {
      if (val === 'strong' || val === 'mild' || val === 'slight') {
        this.showOdorDescription = true;
        if (!this.isLegacy)
          this.form.get('odorDescription').setValidators([Validators.required]);
      } else {
        this.showOdorDescription = false;
        this.form.get('odorDescription').setValue(null);
        this.form.get('odorDescription').clearValidators();
      }
      this.form.get('odorDescription').updateValueAndValidity();
    });
  };

  getSumOfActuals(samples) {
    const typicalPercentValues = _.cloneDeep(
      samples?.map((pc) => Number(pc.typicalOrActual))
    );
    return typicalPercentValues.reduce((a, b) => a + b, 0);
  }
  getSumOfMax(samples) {
    const maxPercentValues = _.cloneDeep(samples?.map((pc) => Number(pc.max)));
    const sum = maxPercentValues.reduce((a, b) => a + b, 0);
    return sum > 100 ? 100 : sum;
  }
  getRemainingActuals() {
    // if the sum is already greater than 100, just return 0
    if (this.sumOfTypical && this.sumOfTypical > 100) return 0;

    // otherwise, return the remaining percent to reach 100
    return this.sumOfTypical && this.sumOfTypical > 0
      ? 100 - this.sumOfTypical
      : 100;
  }
  validateTypicalOrActualPercent = (samples) => {
    if (this.isLegacy === true) return;
    if (!samples || samples.every((a) => !a)) {
      this.sumOfTypical = 0;
      return;
    }

    if (samples.every((a) => a && a?.max)) {
      this.sumOfTypical = this.getSumOfMax(samples);
    } else if (samples.every((a) => a && a?.typicalOrActual)) {
      this.sumOfTypical = this.getSumOfActuals(samples);
    }

    // if the sum is 0 - 99, set the error
    if (this.sumOfTypical < 100 && this.sumOfTypical >= 0) {
      this.form
        .get('physicalComponents')
        .setErrors({ incorrect: true }, { emitEvent: false });
    } else {
      this.form
        .get('physicalComponents')
        .setErrors({ incorrect: false }, { emitEvent: false });
      this.form
        .get('physicalComponents')
        .updateValueAndValidity({ emitEvent: false });
    }
    this.form.updateValueAndValidity();
  };

  public onDominantColorChange(otherColor: MatCheckboxChange) {
    if (otherColor.checked) {
      this.isDominantColorSelected = true;
      if (!this.isLegacy)
        this.form.get('otherColor').setValidators([Validators.required]);
    } else {
      this.isDominantColorSelected = false;
      this.form.get('otherColor').setValue(null);
      this.form.get('otherColor').clearValidators();
    }
    this.form.get('otherColor').updateValueAndValidity();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.profileStatus?.currentValue === 'Not Submitted') {
      ['dominantColor', 'odorStrength'].forEach((control) => {
        this.form.controls[control].removeValidators(Validators.required);
        this.form.updateValueAndValidity();
        this.isRequired = false;
      });
    } else if (
      changes.profileStatus?.currentValue &&
      changes.profileStatus?.currentValue !== 'Not Submitted'
    ) {
      ['dominantColor', 'odorStrength'].forEach((control) => {
        if (this.isLegacy === false)
          this.form.controls[control].addValidators(Validators.required);
        this.form.updateValueAndValidity();
        this.isRequired = true;
      });
    }
  }
}
